import React from 'react';
import { useFormContext } from "react-hook-form";
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  HeartPulseIcon,
  DumbbellIcon,
  WifiIcon,
  CoffeeIcon,
  UtensilsIcon,
  CircleDotIcon,
  DogIcon,
  ToggleRightIcon,
  CalendarDaysIcon,
  TvIcon,
  AirVentIcon,
  ThermometerIcon,
  ShieldIcon,
  WashingMachineIcon,
  ClockIcon,
  CigaretteIcon
} from 'lucide-react';

interface SwitchFieldProps {
  label: string;
  icon: React.ReactNode;
  fieldName: string;
  readOnly?: boolean;
  onChange?: (checked: boolean) => void;
}

const SwitchField = ({ label, icon, fieldName, readOnly = false, onChange }: SwitchFieldProps) => {
  const { setValue, watch } = useFormContext();
  const isChecked = watch(fieldName, false);

  const handleChange = (checked: boolean) => {
    if (!readOnly) {
      setValue(fieldName, checked);
      if (onChange) onChange(checked);
    }
  };

  return (
    <div className="form-group flex flex-row items-center gap-4">
      <Label className="form-label flex items-center flex-row gap-2">
        {icon}
        {label}
      </Label>
      <div className="flex items-center flex-row space-x-2">
        <Switch
          checked={isChecked}
          onCheckedChange={handleChange}
          disabled={readOnly}
        />
        <span>{isChecked ? 'Sí' : 'No'}</span>
      </div>
    </div>
  );
};

interface SwitchesGroupProps {
  readOnly?: boolean;
  onAccessibilityChange?: (checked: boolean) => void;
}

const SwitchesGroup = ({ readOnly = false, onAccessibilityChange }: SwitchesGroupProps) => {
  return (
    <div className="space-y-6 p-6">
      <h3 className="text-lg font-medium">Servicios Básicos</h3>
      <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-5 gap-6">
        <SwitchField 
          label="Spa" 
          icon={<HeartPulseIcon size={16} />} 
          fieldName="services.spa"
          readOnly={readOnly}
        />
        
        <SwitchField 
          label="Gimnasio" 
          icon={<DumbbellIcon size={16} />} 
          fieldName="services.gym"
          readOnly={readOnly}
        />
        
        <SwitchField 
          label="WiFi" 
          icon={<WifiIcon size={16} />} 
          fieldName="services.wifi"
          readOnly={readOnly}
        />
        
        <SwitchField 
          label="Desayuno" 
          icon={<CoffeeIcon size={16} />} 
          fieldName="services.breakfast"
          readOnly={readOnly}
        />
        
        <SwitchField 
          label="Casino" 
          icon={<CircleDotIcon size={16} />} 
          fieldName="services.casino"
          readOnly={readOnly}
        />
        
        <SwitchField 
          label="Admite mascotas" 
          icon={<DogIcon size={16} />} 
          fieldName="services.petsAllowed"
          readOnly={readOnly}
        />
        
        <SwitchField 
          label="Accesibilidad" 
          icon={<ToggleRightIcon size={16} />} 
          fieldName="isAccessible"
          readOnly={readOnly}
          onChange={onAccessibilityChange}
        />
        
        <SwitchField 
          label="Cafetería/Bar/Snackbar" 
          icon={<CoffeeIcon size={16} />} 
          fieldName="services.cafeteriaBar"
          readOnly={readOnly}
        />
        
        <SwitchField 
          label="Restaurante" 
          icon={<UtensilsIcon size={16} />} 
          fieldName="services.restaurant"
          readOnly={readOnly}
        />
        
        <SwitchField
          label="Salón de eventos"
          icon={<CalendarDaysIcon size={16} />}
          fieldName="services.eventRoom"
          readOnly={readOnly}
        />

        <SwitchField
          label="TV"
          icon={<TvIcon size={16} />}
          fieldName="services.tv"
          readOnly={readOnly}
        />

        <SwitchField
          label="Aire Acondicionado"
          icon={<AirVentIcon size={16} />}
          fieldName="services.airConditioning"
          readOnly={readOnly}
        />

        <SwitchField
          label="Calefacción"
          icon={<ThermometerIcon size={16} />}
          fieldName="services.heating"
          readOnly={readOnly}
        />

        <SwitchField
          label="Caja Fuerte"
          icon={<ShieldIcon size={16} />}
          fieldName="services.safeBox"
          readOnly={readOnly}
        />

        <SwitchField
          label="Servicio de Lavandería"
          icon={<WashingMachineIcon size={16} />}
          fieldName="services.laundryService"
          readOnly={readOnly}
        />

        <SwitchField
          label="Recepción 24h"
          icon={<ClockIcon size={16} />}
          fieldName="services.frontdesk24h"
          readOnly={readOnly}
        />

        <SwitchField
          label="Área de Fumadores"
          icon={<CigaretteIcon size={16} />}
          fieldName="services.smokingArea"
          readOnly={readOnly}
        />
      </div>
    </div>
  );
};

export default SwitchesGroup;
export { SwitchField };
